import { useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../components/content/axiosConfig';
import { resolveEffortUnits } from '../../utils/samplingCpueUtils';
import { queryKeys } from './queryKeys';

export const ENRICH_SESSIONS_CHUNK_SIZE = 200;

/** Normalize fish type filter to a stable comma-separated string of IDs. */
export function normalizeFishTypesKey(fishTypes) {
  if (Array.isArray(fishTypes)) {
    return fishTypes
      .map((f) => (f && typeof f === 'object' ? f.value : f))
      .filter((v) => v != null && v !== '')
      .map(String)
      .sort()
      .join(',');
  }
  if (typeof fishTypes === 'string') {
    return fishTypes
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .sort()
      .join(',');
  }
  return '';
}

export function normalizePondDIDsKey(pondDIDs) {
  return [...new Set((pondDIDs || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))]
    .sort((a, b) => a - b)
    .join(',');
}

/**
 * @param {object} opts
 * @param {number|string} [opts.pondId]
 * @param {string} [opts.startDate]
 * @param {string} [opts.endDate]
 * @param {boolean} [opts.useAllData]
 * @param {number[]|string} opts.pondDIDs
 * @param {string|array} [opts.fishTypes]
 */
export function buildSamplingEnrichKey({
  pondId,
  startDate,
  endDate,
  useAllData,
  pondDIDs,
  fishTypes,
} = {}) {
  return {
    pondId: pondId ?? null,
    startDate: useAllData ? null : startDate ?? null,
    endDate: useAllData ? null : endDate ?? null,
    useAllData: Boolean(useAllData),
    pondDIDs: normalizePondDIDsKey(pondDIDs),
    fishTypes: normalizeFishTypesKey(fishTypes),
  };
}

async function postEnrichSessionsChunk(pondDIDs, fishTypes) {
  const response = await axiosInstance.post('/api/PondDetail/EnrichSessions', {
    pondDIDs,
    fishTypes: fishTypes || '',
  });
  return Array.isArray(response.data) ? response.data : [];
}

/** POST EnrichSessions, chunking at ENRICH_SESSIONS_CHUNK_SIZE. */
export async function fetchEnrichSessions(pondDIDs, fishTypes = '') {
  const ids = [...new Set((pondDIDs || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))];
  if (!ids.length) return [];

  const fishTypesKey = normalizeFishTypesKey(fishTypes);
  const chunks = [];
  for (let i = 0; i < ids.length; i += ENRICH_SESSIONS_CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + ENRICH_SESSIONS_CHUNK_SIZE));
  }

  const results = [];
  for (const chunk of chunks) {
    const rows = await postEnrichSessionsChunk(chunk, fishTypesKey);
    results.push(...rows);
  }
  return results;
}

export function mergeEnrichIntoDetails(details, enrichRows) {
  const byId = new Map((enrichRows || []).map((row) => [Number(row.id), row]));
  return (details || []).map((detail) => {
    const enrich = byId.get(Number(detail.id));
    if (!enrich) {
      return {
        ...detail,
        EffortUnits: detail.EffortUnits ?? [],
        SamplingData: [],
        FishType: [],
        PondNotes: [],
      };
    }
    return {
      ...detail,
      EffortUnits: resolveEffortUnits(detail, enrich.EffortUnits),
      SamplingData: enrich.SamplingData || [],
      FishType: enrich.FishType || [],
      PondNotes: enrich.PondNotes || [],
    };
  });
}

/**
 * Fetch enrich rows via React Query cache (staleTime ~60s from queryClient defaults).
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {object} keyParts - buildSamplingEnrichKey args (must include pondDIDs)
 */
export async function fetchSamplingEnrichmentCached(queryClient, keyParts) {
  const key = buildSamplingEnrichKey(keyParts);
  const pondDIDs = (key.pondDIDs ? key.pondDIDs.split(',') : []).map(Number).filter(Boolean);
  return queryClient.fetchQuery({
    queryKey: queryKeys.samplingEnrich(key),
    queryFn: () => fetchEnrichSessions(pondDIDs, key.fishTypes),
    staleTime: 60_000,
    // Keep enrich payloads briefly; Graph View clears them on chart switch via removeQueries.
    gcTime: 30_000,
  });
}

/** Enrich pond detail rows (GetPondDetails) with one cached EnrichSessions call. */
export async function enrichPondDetailsCached(queryClient, details, keyParts = {}) {
  const list = details || [];
  if (!list.length) return [];
  const pondDIDs = list.map((d) => d.id).filter((id) => id != null);
  const enrichRows = await fetchSamplingEnrichmentCached(queryClient, {
    ...keyParts,
    pondDIDs,
  });
  return mergeEnrichIntoDetails(list, enrichRows);
}

export function useSamplingEnrichment(keyParts, options = {}) {
  const key = buildSamplingEnrichKey(keyParts);
  const pondDIDs = (key.pondDIDs ? key.pondDIDs.split(',') : []).map(Number).filter(Boolean);
  return useQuery({
    queryKey: queryKeys.samplingEnrich(key),
    queryFn: () => fetchEnrichSessions(pondDIDs, key.fishTypes),
    enabled: pondDIDs.length > 0 && options.enabled !== false,
    staleTime: 60_000,
    ...options,
  });
}

export function useInvalidateSamplingEnrichment() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['samplingEnrich'] });
}
